/** @jsxImportSource @emotion/react */
import React, { useState, useCallback } from 'react';
import { Alert, Button, Card, Space, Spin, Tag, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import commonStyles from '../../../../styles/commonStyles';
import EChart from '../../../../components/charts/EChart';
import HarvestForecastCard from './HarvestForecastCard';
import LifecycleProgressCard from './LifecycleProgressCard';
import MetricSelector from './MetricSelector';
import { useForecastChart } from '../hooks/useForecastChart';
import { styles } from '../styles';
import type { ForecastMetricKey } from '../constants';

const { Text } = Typography;

interface Props {
  forecastSeries: any;
  loadingForecastSeries: boolean;
  notPlantedDays: number;
  lifecycleTerminalInfo: { terminalStatus: string; terminalDate: string | null; terminalReason: string | null } | null;
  harvestForecast: any;
  isForecastAnnualLike: boolean;
  harvestGrossTargetKg: number | null;
  harvestMarketableTargetKg: number | null;
  plantingQuantity: number;
  showBatchQuantityMetric?: boolean;
  initialMetric?: ForecastMetricKey;
}

const ForecastCard: React.FC<Props> = ({
  forecastSeries,
  loadingForecastSeries,
  notPlantedDays,
  lifecycleTerminalInfo,
  harvestForecast,
  isForecastAnnualLike,
  harvestGrossTargetKg,
  harvestMarketableTargetKg,
  plantingQuantity,
  showBatchQuantityMetric = false,
  initialMetric = 'dimensions.height',
}) => {
  const { saveId } = useParams<{ saveId: string }>();
  const [selectedMetric, setSelectedMetric] = useState<ForecastMetricKey>(initialMetric);
  const [showHarvestDailyBars, setShowHarvestDailyBars] = useState(false);
  const [showOptimalZone, setShowOptimalZone] = useState(true);
  const timeSeriesStatus = forecastSeries?.timeSeriesStatus || 'valid';
  const hasReplayingTimeSeries = timeSeriesStatus === 'replaying'
    || forecastSeries?.replayJobStatus === 'queued'
    || forecastSeries?.replayJobStatus === 'running';

  const forecastChartOption = useForecastChart({
    forecastSeries,
    selectedMetric,
    showOptimalZone,
    showHarvestDailyBars,
  });
  const lifecycleWithBbch = forecastSeries?.finalState?.lifecycle
    ? {
        ...forecastSeries.finalState.lifecycle,
        bbch: forecastSeries.finalState.bbch ?? forecastSeries.finalState.lifecycle.bbch ?? null,
      }
    : null;

  const handleSelectMetric = useCallback((key: ForecastMetricKey) => {
    setShowHarvestDailyBars(false);
    setSelectedMetric(key);
  }, []);

  const handleToggleHarvestBars = useCallback(() => {
    setShowHarvestDailyBars((prev) => !prev);
  }, []);

  return (
    <Card title="Прогноз симуляции" extra={<Tag color="purple">Активная сессия</Tag>}>
      {loadingForecastSeries ? (
        <Spin />
      ) : hasReplayingTimeSeries ? (
        <Alert
          type="info"
          showIcon
          message="Прогноз пересчитывается"
          description="График обновится автоматически после фонового пересчёта."
        />
      ) : !forecastSeries?.timeSeries?.length ? (
        <Text type="secondary">Недостаточно данных симуляции для построения графика</Text>
      ) : (
        <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
          <EChart option={forecastChartOption} height={320} />

          {notPlantedDays > 0 && (
            <Text type="secondary">
              На графике выделено {notPlantedDays} дн. до фактической даты высадки (режим «ещё не высажено»).
            </Text>
          )}

          <LifecycleProgressCard lifecycle={lifecycleWithBbch} />

          {lifecycleTerminalInfo?.terminalDate && (
            <Text type={lifecycleTerminalInfo.terminalStatus === 'dead' ? 'danger' : 'success'}>
              {lifecycleTerminalInfo.terminalStatus === 'dead'
                ? `☠ Живых растений не осталось к ${dayjs(lifecycleTerminalInfo.terminalDate).format('DD.MM.YYYY')}`
                : `✓ Жизненный цикл завершён к ${dayjs(lifecycleTerminalInfo.terminalDate).format('DD.MM.YYYY')}`}
              {lifecycleTerminalInfo.terminalReason ? ` · причина: ${lifecycleTerminalInfo.terminalReason}` : ''}
              {lifecycleTerminalInfo.terminalStatus ? ` · статус: ${lifecycleTerminalInfo.terminalStatus}` : ''}
            </Text>
          )}

          <HarvestForecastCard
            harvestForecast={harvestForecast}
            isForecastAnnualLike={isForecastAnnualLike}
            harvestGrossTargetKg={harvestGrossTargetKg}
            harvestMarketableTargetKg={harvestMarketableTargetKg}
            plantingQuantity={plantingQuantity}
          />

          <MetricSelector
            selectedMetric={selectedMetric}
            showHarvestDailyBars={showHarvestDailyBars}
            showOptimalZone={showOptimalZone}
            onSelectMetric={handleSelectMetric}
            onToggleHarvestBars={handleToggleHarvestBars}
            onToggleOptimalZone={setShowOptimalZone}
            plantingQuantity={plantingQuantity}
            showBatchQuantityMetric={showBatchQuantityMetric}
          />

          {saveId && (
            <Space size={8} align="center">
              <Text type="secondary" css={styles.smallText}>Склад:</Text>
              <Link to={`/saves/${saveId}/storage`}>
                <Button size="small">Открыть остатки и передержку</Button>
              </Link>
            </Space>
          )}

          {!!forecastSeries?.stressGuide && (
            <Text type="secondary" css={styles.smallText}>
              Рамки модели:
              {forecastSeries.stressGuide.temperature?.optMinC != null && forecastSeries.stressGuide.temperature?.optMaxC != null
                ? ` температура ${forecastSeries.stressGuide.temperature.optMinC}–${forecastSeries.stressGuide.temperature.optMaxC}°C;`
                : ''}
              {forecastSeries.stressGuide.soil?.optSoilMoisturePct != null
                ? ` влага почвы ~${forecastSeries.stressGuide.soil.optSoilMoisturePct}%`
                : ''}
              {forecastSeries.stressGuide.soil?.minSoilMoisturePct != null && forecastSeries.stressGuide.soil?.maxSoilMoisturePct != null
                ? ` (допустимо ${forecastSeries.stressGuide.soil.minSoilMoisturePct}–${forecastSeries.stressGuide.soil.maxSoilMoisturePct}%);`
                : ''}
              {forecastSeries.stressGuide.light?.defaultDliMinMol != null && forecastSeries.stressGuide.light?.defaultDliTargetMol != null
                ? ` свет ${forecastSeries.stressGuide.light.defaultDliMinMol}-${forecastSeries.stressGuide.light.defaultDliTargetMol} mol/m²/д;`
                : ''}
              {forecastSeries.stressGuide.light?.defaultPhotoperiodTargetHours != null
                ? ` базовый фотопериод ~${forecastSeries.stressGuide.light.defaultPhotoperiodTargetHours} ч;`
                : ''}
              {forecastSeries.stressGuide.careTargets?.watering?.triggerPct != null
                ? ` полив при влаге ниже ${forecastSeries.stressGuide.careTargets.watering.triggerPct}%`
                : ''}
              {forecastSeries.stressGuide.careTargets?.watering?.targetPct != null
                ? `, цель ${forecastSeries.stressGuide.careTargets.watering.targetPct}%`
                : ''}
            </Text>
          )}
        </Space>
      )}
    </Card>
  );
};

export default ForecastCard;
